import { useEffect, useState } from 'react'
import { authApi, type ShopTemplate } from '../lib/api'
import { useApp } from '../lib/store'

type Step = 'phone' | 'code' | 'shop'

/**
 * Đăng nhập bằng số điện thoại. Không email, không mật khẩu — chủ quán không có
 * thói quen dùng email, và mật khẩu là thứ họ sẽ quên rồi gọi điện nhờ hỗ trợ,
 * mà gói 50k thì không nuôi nổi tổng đài.
 */
export function LoginScreen() {
  const signIn = useApp((s) => s.signIn)
  const say = useApp((s) => s.say)

  const [step, setStep] = useState<Step>('phone')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [devCode, setDevCode] = useState<string | null>(null)
  const [shopName, setShopName] = useState('')
  const [template, setTemplate] = useState('cafe')
  const [templates, setTemplates] = useState<ShopTemplate[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    authApi.templates().then(setTemplates).catch(() => setTemplates([]))
  }, [])

  async function requestOtp() {
    setBusy(true)
    setError(null)
    try {
      const res = await authApi.requestOtp(phone)
      setDevCode(res.devCode)
      if (res.devCode) setCode(res.devCode)
      setStep('code')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Không gửi được mã')
    }
    setBusy(false)
  }

  async function verify(withShop = false) {
    setBusy(true)
    setError(null)
    try {
      const res = await authApi.verify(
        phone,
        code,
        withShop ? shopName : undefined,
        withShop ? template : undefined,
      )
      await signIn(
        {
          token: res.token,
          userId: res.userId,
          name: res.name,
          role: res.role,
          expiresAt: res.expiresAt,
        },
        res.shop,
      )
      say(res.isNewShop ? `Đã tạo ${res.shop.name}` : `Chào ${res.shop.name}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Mã không đúng')
      setBusy(false)
    }
  }

  return (
    <div className="screen login">
      <div className="body center">
        <div className="login-card">
          <div className="login-mark" aria-hidden="true" />
          <h1>sigoPOS</h1>

          {step === 'phone' && (
            <>
              <p className="login-lede">
                Máy tính tiền cho quán nhỏ. Nhập số điện thoại, chúng tôi gửi mã 4 số.
              </p>
              <label className="field">
                <span>Số điện thoại</span>
                <input
                  type="tel"
                  inputMode="tel"
                  autoFocus
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="0912 345 678"
                  onKeyDown={(e) => e.key === 'Enter' && phone && void requestOtp()}
                />
              </label>
              <button
                type="button"
                className="btn btn-brand btn-lg wide"
                disabled={busy || phone.length < 9}
                onClick={requestOtp}
              >
                {busy ? 'Đang gửi…' : 'Gửi mã'}
              </button>
            </>
          )}

          {step === 'code' && (
            <>
              <p className="login-lede">
                Mã đã gửi tới <b>{phone}</b>.
                {devCode && <em className="dev-hint"> Máy thử — mã là {devCode}.</em>}
              </p>
              <label className="field">
                <span>Mã xác nhận</span>
                <input
                  inputMode="numeric"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="1234"
                  maxLength={6}
                  onKeyDown={(e) => e.key === 'Enter' && void verify()}
                />
              </label>
              <button
                type="button"
                className="btn btn-brand btn-lg wide"
                disabled={busy || code.length < 4}
                onClick={() => verify()}
              >
                {busy ? 'Đang vào…' : 'Vào bán hàng'}
              </button>
              <button type="button" className="link-btn" onClick={() => setStep('shop')}>
                Quán mới, chưa có tài khoản
              </button>
            </>
          )}

          {step === 'shop' && (
            <>
              <p className="login-lede">Chọn kiểu quán — có sẵn menu và nút chi phí, khỏi gõ tay.</p>
              <label className="field">
                <span>Tên quán</span>
                <input
                  autoFocus
                  value={shopName}
                  onChange={(e) => setShopName(e.target.value)}
                  placeholder="Cà phê Sáu Lúa"
                  maxLength={120}
                />
              </label>

              <div className="template-list">
                {templates.map((t) => (
                  <button
                    key={t.code}
                    type="button"
                    className="template"
                    aria-pressed={t.code === template}
                    onClick={() => setTemplate(t.code)}
                  >
                    <b>{t.label}</b>
                    <small>{t.description}</small>
                    <em>{t.productCount} món dựng sẵn</em>
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="btn btn-brand btn-lg wide"
                disabled={busy || !shopName.trim()}
                onClick={() => verify(true)}
              >
                {busy ? 'Đang tạo…' : 'Tạo quán'}
              </button>
            </>
          )}

          {error && <p className="login-error">{error}</p>}
        </div>
      </div>
    </div>
  )
}
